// client/src/components/tendermaker/CollapsibleTopStrip.js
import React, { useState, useEffect } from "react";

const card = {
  border: "1px solid #e5e7eb",
  borderRadius: 14,
  padding: 10,
  background: "white",
};
const cardSoft = { ...card, background: "#fafafa" };
const btn = {
  padding: "8px 10px",
  borderRadius: 10,
  border: "1px solid #e5e7eb",
  background: "#fff",
  cursor: "pointer",
  fontWeight: 900,
  fontSize: 12.5,
};
const btnSoft = { ...btn, background: "#f9fafb" };
const tiny = { color: "#6b7280", fontSize: 12 };
const titleSm = { fontWeight: 1000, fontSize: 12.5, color: "#111827" };

function fmtTime(d) {
  try {
    if (!d) return "-";
    const dt = typeof d === "string" ? new Date(d) : d?.toDate?.() ? d.toDate() : new Date(d);
    if (Number.isNaN(dt.getTime())) return "-";
    return dt.toLocaleString();
  } catch {
    return "-";
  }
}

export default function CollapsibleTopStrip({
  bids,
  selectedBidId,
  setSelectedBidId,
  mode,
  setMode,
  busy,
  status,
  run,
  currentRunId,
  history,
  histBusy,
  histHasMore,
  histStatusFilter,
  selectedBid,
  latestRunId,
  onRunExtract,
  onRefreshBids,
  onOpenLatestDone,
  onLoadHistory,
  onOpenRun,
  onSetHistStatusFilter,
  navigate,
}) {
  const [collapsed, setCollapsed] = useState(false);

  // Auto-collapse when a run is loaded
  useEffect(() => {
    if (run?.id && run?.status === "done") {
      setCollapsed(true);
    }
  }, [run?.id, run?.status]);

  const reqCount = run?.merged
    ? (run.merged.matched_requirements?.length || 0) +
      (run.merged.forms_annexures?.length || 0) +
      (run.merged.unknown_requirements?.length || 0)
    : 0;

  if (collapsed) {
    return (
      <div
        style={{
          ...card,
          marginTop: 12,
          marginBottom: 12,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
          padding: "8px 14px",
        }}
      >
        <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap", fontSize: 12.5 }}>
          <span style={tiny}>
            Bid: <b style={{ color: "#111827" }}>{selectedBid?.title || selectedBidId || "-"}</b>
          </span>
          <span style={{ color: "#d1d5db" }}>|</span>
          <span style={tiny}>
            Run:{" "}
            <b style={{ color: "#111827", fontFamily: "monospace" }}>
              {currentRunId ? currentRunId.slice(0, 12) + "..." : "-"}
            </b>{" "}
            (<b style={{ color: status === "done" ? "#16a34a" : status === "error" ? "#dc2626" : "#111827" }}>
              {status || "-"}
            </b>)
          </span>
          <span style={{ color: "#d1d5db" }}>|</span>
          <span style={tiny}>
            <b style={{ color: "#111827" }}>{reqCount}</b> requirements
          </span>
          <span style={{ color: "#d1d5db" }}>|</span>
          <span style={tiny}>
            Mode: <b style={{ color: "#111827" }}>{mode}</b>
          </span>
        </div>

        <button
          onClick={() => setCollapsed(false)}
          style={{ ...btnSoft, fontSize: 11, padding: "5px 10px" }}
        >
          Expand
        </button>
      </div>
    );
  }

  return (
    <div style={{ marginTop: 12, marginBottom: 12 }}>
      {/* Collapse button row */}
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 4 }}>
        <button
          onClick={() => setCollapsed(true)}
          style={{ ...btnSoft, fontSize: 11, padding: "4px 10px" }}
        >
          Minimize
        </button>
      </div>

      {/* 3-card grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(380px, 1.2fr) minmax(260px, 0.8fr) minmax(340px, 1fr)",
          gap: 12,
          alignItems: "start",
        }}
      >
        {/* 1) Select + Actions */}
        <div style={card}>
          <div style={titleSm}>Bid + Mode</div>

          <div style={{ display: "grid", gap: 8, marginTop: 8 }}>
            <select
              value={selectedBidId}
              onChange={(e) => setSelectedBidId(e.target.value)}
              style={{ padding: 8, borderRadius: 10, border: "1px solid #e5e7eb", fontSize: 12.5 }}
              disabled={busy}
            >
              <option value="">-- Choose a bid --</option>
              {bids.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.title} • {b.id}
                </option>
              ))}
            </select>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <select
                value={mode}
                onChange={(e) => setMode(e.target.value)}
                disabled={busy}
                style={{ padding: 8, borderRadius: 10, border: "1px solid #e5e7eb", fontSize: 12.5 }}
              >
                <option value="auto">auto (text then OCR)</option>
                <option value="text">text only</option>
                <option value="ocr">OCR only</option>
              </select>

              <button onClick={onRunExtract} style={btn} disabled={busy || !selectedBidId}>
                {busy ? "Working..." : "Run Extraction"}
              </button>

              <button onClick={onRefreshBids} style={btnSoft} disabled={busy}>
                Refresh Bids
              </button>
            </div>

            <div style={tiny}>
              {selectedBid ? (
                <>
                  Selected: <span style={{ color: "#111827", fontWeight: 900 }}>{selectedBid.title}</span>
                </>
              ) : (
                <span>Pick a bid to begin.</span>
              )}
            </div>
          </div>
        </div>

        {/* 2) Current run status */}
        <div style={cardSoft}>
          <div style={titleSm}>Current Run</div>

          <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
            <div style={{ display: "grid", gap: 4 }}>
              <div style={tiny}>
                Status: <b style={{ color: "#111827" }}>{status || "-"}</b>
              </div>

              <div style={tiny}>
                runId:{" "}
                <b style={{ color: "#111827", fontFamily: "monospace" }}>
                  {currentRunId || "-"}
                </b>
              </div>
            </div>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button style={btnSoft} disabled={!selectedBidId || histBusy} onClick={onOpenLatestDone}>
                Open latest DONE
              </button>

              <button
                style={btnSoft}
                disabled={!selectedBidId || histBusy}
                onClick={() => onLoadHistory({ reset: true })}
              >
                {histBusy ? "Loading..." : "Refresh Runs"}
              </button>
            </div>

            <div style={tiny}>
              Tip: Use <b>Runs</b> card to open older runs.
            </div>
          </div>
        </div>

        {/* 3) Runs */}
        <div style={card}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
            <div style={titleSm}>Runs</div>

            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <select
                value={histStatusFilter}
                onChange={(e) => onSetHistStatusFilter(e.target.value)}
                style={{ padding: 7, borderRadius: 10, border: "1px solid #e5e7eb", fontSize: 12.5 }}
                disabled={histBusy || !selectedBidId}
              >
                <option value="">all</option>
                <option value="queued">queued</option>
                <option value="running">running</option>
                <option value="done">done</option>
                <option value="error">error</option>
              </select>

              <button
                style={btnSoft}
                disabled={histBusy || !selectedBidId}
                onClick={() => onLoadHistory({ reset: true })}
              >
                {histBusy ? "Loading..." : "Reload"}
              </button>
            </div>
          </div>

          <div style={{ marginTop: 10 }}>
            {!selectedBidId ? (
              <div style={tiny}>Select a bid to see runs.</div>
            ) : history.length === 0 ? (
              <div style={tiny}>{histBusy ? "Loading..." : "No runs."}</div>
            ) : (
              <div style={{ display: "grid", gap: 8 }}>
                {history.slice(0, 5).map((h) => (
                  <div
                    key={h.id}
                    style={{
                      border: "1px solid #e5e7eb",
                      borderRadius: 12,
                      padding: 10,
                      background: "#fff",
                      display: "flex",
                      gap: 10,
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      alignItems: "center",
                    }}
                  >
                    <div style={{ minWidth: 220 }}>
                      <div style={{ fontWeight: 1000, color: "#111827", fontSize: 12.5 }}>
                        <span style={{ fontFamily: "monospace" }}>{h.id}</span>
                      </div>
                      <div style={{ ...tiny, marginTop: 3 }}>
                        <b style={{ color: "#111827" }}>{h.status || "-"}</b> • {fmtTime(h.createdAt)}
                      </div>
                    </div>

                    <button
                      style={btn}
                      onClick={() =>
                        navigate(
                          `/tendermaker/bid-studio?bidId=${encodeURIComponent(selectedBidId)}&runId=${encodeURIComponent(h.id)}`
                        )
                      }
                      disabled={busy}
                    >
                      Open
                    </button>
                  </div>
                ))}

                {histHasMore ? (
                  <button
                    style={btnSoft}
                    disabled={histBusy}
                    onClick={() => onLoadHistory({ reset: false })}
                  >
                    {histBusy ? "Loading..." : "Load more"}
                  </button>
                ) : null}
              </div>
            )}
          </div>

          {selectedBidId && latestRunId ? (
            <div style={{ ...tiny, marginTop: 8 }}>
              Latest in list: <b style={{ fontFamily: "monospace", color: "#111827" }}>{latestRunId}</b>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
